import React, {useCallback, useEffect, useState} from 'react';
import {createPortal} from 'react-dom';
import {createQrLoginInvite, getQrLoginInviteStatus} from '../../utils/requestUtils';
import {usePermission} from '../../core/PermissionContext';
import {UserPermission} from '../../utils/utils';

/** Maiúsculas: letras (incluindo acentos), números, espaço, hífen e apóstrofo. */
function sanitizeQrUserNameInput(value) {
    const upper = value.toUpperCase();
    return [...upper]
        .filter((ch) => /[A-ZÀ-ÖØ-Ý0-9]/.test(ch) || /\s/.test(ch) || ch === '-' || ch === "'")
        .join('');
}

const ACCESS_LEVELS = [
    {label: 'Básico', permissions: [UserPermission.PDV_VIEW]},
    {label: 'Intermediário', permissions: [UserPermission.PDV_VIEW, UserPermission.PDV_MANAGE]},
    {label: 'Avançado', permissions: [UserPermission.PDV_VIEW, UserPermission.PDV_MANAGE, UserPermission.PDV_ADMIN]},
];

const QrInviteModal = ({onClose}) => {
    const {hasUserPermission} = usePermission();
    const allowedLevels = ACCESS_LEVELS.filter(level => level.permissions.every(p => hasUserPermission(p)));

    const [name, setName] = useState('');
    const [levelIndex, setLevelIndex] = useState(allowedLevels.length - 1);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);

    const submitInvite = async () => {
        const trimmed = name.trim();
        if (!trimmed) {
            setError('Informe o nome do novo usuário.');
            return;
        }
        const level = allowedLevels[levelIndex];
        if (!level) {
            setError('Nível de acesso inválido.');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const data = await createQrLoginInvite(trimmed, level.permissions, window.location.origin);
            setResult(data);
        } catch (e) {
            setError(e.message || 'Não foi possível gerar o convite.');
        } finally {
            setLoading(false);
        }
    };

    const closeModal = useCallback(() => {
        setResult(null);
        setError(null);
        onClose();
    }, [onClose]);

    useEffect(() => {
        if (!result?.loginUrl) return undefined;
        let hash;
        try {
            hash = new URL(result.loginUrl).searchParams.get('hash');
        } catch (e) {
            return undefined;
        }
        if (!hash) return undefined;

        const poll = async () => {
            try {
                const status = await getQrLoginInviteStatus(hash);
                if (status.consumed) {
                    closeModal();
                }
            } catch (e) { /* ignora falhas pontuais entre um poll e outro */ }
        };
        const id = setInterval(poll, 2000);
        poll();
        return () => clearInterval(id);
    }, [result, closeModal]);

    return createPortal(
        <div className="modal-overlay" style={{zIndex: 10500}}>
            <div className="modal-content dialog-box">
                <p className="dialog-message">{result ? 'QR Code de login' : 'Garçom · QR Code'}</p>

                {!result && (
                    <>
                        <p style={{fontSize: 12, color: '#555'}}>
                            Nome em letras maiúsculas (pode incluir números) · 1 acesso · 48h
                        </p>
                        <input
                            type="text"
                            autoComplete="name"
                            style={{width: '100%', boxSizing: 'border-box', padding: '10px', marginBottom: '8px'}}
                            value={name}
                            onChange={(e) => setName(sanitizeQrUserNameInput(e.target.value))}
                            disabled={loading}
                            placeholder="NOME (LETRAS E NÚMEROS)"
                            spellCheck={false}
                        />
                        <select
                            style={{width: '100%', boxSizing: 'border-box', padding: '10px', marginBottom: '8px'}}
                            value={levelIndex}
                            onChange={(e) => setLevelIndex(Number(e.target.value))}
                            disabled={loading}
                        >
                            {allowedLevels.map((level, index) => (
                                <option key={level.label} value={index}>{level.label}</option>
                            ))}
                        </select>
                    </>
                )}

                {error && <p style={{color: '#c00', fontSize: 13}}>{error}</p>}

                {result && (
                    <div style={{textAlign: 'center'}}>
                        <p><strong>{result.userName}</strong></p>
                        <p style={{fontSize: 12, color: '#666'}}>Escaneie com a câmera do celular</p>
                        <img
                            alt="QR Code de login"
                            style={{width: '100%', maxWidth: 280, height: 'auto'}}
                            src={`data:image/png;base64,${result.qrCodePngBase64}`}
                        />
                    </div>
                )}

                <div className="dialog-actions">
                    <button className="back-button" onClick={closeModal}>{result ? 'Fechar' : 'Cancelar'}</button>
                    {!result && (
                        <button onClick={submitInvite} disabled={loading}>
                            {loading ? 'Gerando…' : 'Gerar QR'}
                        </button>
                    )}
                </div>
            </div>
        </div>,
        document.body
    );
};

export default QrInviteModal;
